import { NumberBadge } from '@mesa-labs/mesa-ui';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { openActionItemCount, pageVendorId, vendorOptions } from '../../../utils/vendorActionItems';
import EntityDrawer from '../../common/EntityDrawer';
import ActionItemDetail, { ActionItemDetailHeader } from './components/ActionItemDetail';
import CreateActionItemForm, { createActionItemTitle } from './components/CreateActionItemForm';
import QuickList from './components/QuickList';
import {
  useActionItemsBoardPanel, useCanWriteActionItems, useMeEmail, useMyOpenActionItems, useOperators,
} from './hooks';

const Wrapper = styled.div`
  position: relative;
`;

const IconButton = styled.button<{ $open: boolean }>`
  background: ${(p) => (p.$open ? '#FAFAFA' : 'transparent')};
  border: none;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  padding: 10px 14px;
  position: relative;

  &:hover { background: #FAFAFA; }
`;

const Popup = styled.div`
  padding-top: 6px;
  position: absolute;
  right: 0;
  top: 100%;
  z-index: 60;
`;

// The nav's Action Items button: a badge with the viewer's open count that toggles a quick list of those items, with
// their detail and create drawer. Renders nothing for non-operators.
export default function ActionItemsNavMenu(): React.ReactElement | null {
  const location = useLocation();
  const items = useMyOpenActionItems();
  const me = useMeEmail();
  const canWrite = useCanWriteActionItems();
  const [open, setOpen] = useState(false);
  const [createVendorId, setCreateVendorId] = useState<string | undefined>();

  const {
    writes, panel, selected, selectedVendorName, selectedActions, showDetail, startCreate, close: closeDrawer,
  } = useActionItemsBoardPanel(items ?? []);
  // Assignee pickers live in the drawers, so load operators only once one opens (and never for non-operators).
  const operators = useOperators(!items || panel.kind === 'none');
  // The create form's Vendor picks: the Vendors of the viewer's open items.
  const vendorQuickOptions = useMemo(() => vendorOptions(items ?? []), [items]);

  // Outside click or Esc closes the list. "Inside" is the React tree, not the DOM: the rows' status menus are portaled
  // to the body, but their mousedown still bubbles through React to the wrapper, which runs before this document
  // listener.
  const pressedInside = useRef(false);
  useEffect(() => {
    if (!open) return undefined;
    const outside = () => {
      if (!pressedInside.current) setOpen(false);
      pressedInside.current = false;
    };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', outside);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', outside);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  if (!items) return null;
  const count = openActionItemCount(items);

  return (
    <>
      <Wrapper onMouseDown={() => { pressedInside.current = true; }}>
        <IconButton
          type="button"
          $open={open}
          title="Action Items"
          aria-label={`Action Items, ${count} open`}
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        >
          <img src="/assets/clipboard-icon.svg" alt="" />
          {count > 0 && <NumberBadge num={count} top={2} right={2} size={16} fontSize={10} />}
        </IconButton>
        {open && (
          <Popup>
            <QuickList
              items={items}
              canWrite={canWrite}
              writes={writes}
              onOpenDetail={(item) => { setOpen(false); showDetail(item); }}
              onCreate={() => {
                setOpen(false);
                setCreateVendorId(pageVendorId(location.pathname, location.search));
                startCreate();
              }}
            />
          </Popup>
        )}
      </Wrapper>

      {/* Portaled: the nav's backdrop-filter would otherwise contain the fixed drawer inside the header. */}
      {createPortal(
        <EntityDrawer
          open={!!selected || panel.kind === 'create'}
          onClose={closeDrawer}
          modal={false}
          width={720}
          maxWidth="67%"
          title={selected
            ? <ActionItemDetailHeader item={selected} vendorName={selectedVendorName ?? ''} canWrite={canWrite} actions={selectedActions} />
            : panel.kind === 'create' && createActionItemTitle()}
        >
          {selected && (
            <ActionItemDetail
              item={selected}
              me={me}
              operators={operators}
              canWrite={canWrite}
              actions={selectedActions}
              onOpenItem={(id) => showDetail({ id, vendorId: selected.vendorId })}
            />
          )}
          {panel.kind === 'create' && (
            <CreateActionItemForm
              vendorPicker={{
                vendorId: createVendorId, onChange: setCreateVendorId, quickOptions: vendorQuickOptions, onOpenItem: showDetail,
              }}
              me={me}
              operators={operators}
              error={writes.error}
              onCreate={async (request) => {
                if (!createVendorId) return;
                const created = await writes.create(createVendorId, request);
                if (created) showDetail(created);
              }}
              onCancel={closeDrawer}
            />
          )}
        </EntityDrawer>,
        document.body,
      )}
    </>
  );
}
